/* eslint-disable no-plusplus */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Col, Form, FormGroup, Input, Row } from 'reactstrap';

import SearchInput from 'components/layout/forms/SearchInput';
import BasicTable from 'components/layout/tables/BasicTable';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import { WRITE_CONTACTS } from 'services/enums/users/permissions';
import useHasPermission from 'services/users/useHasPermission';
import useContactTypeOptions from 'services/contacts/useContactTypeOptions';
import { addMessage } from 'store/messages/actions';
import customerApi from '../../api/contacts';
import translations from '../../services/translations/translations';
import '../../style/contacts/Contacts.css';
import '../../style/layout/Table.css';

const Contacts = (): JSX.Element | null => {
  const { t } = useTranslation([translations.contacts, translations.common]);
  const [contactData, setContactData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(50);
  const [totalSize, setTotalSize] = useState(100);
  const [offset, setOffset] = useState(0);
  const [contactTypes, setContactTypes] = useState<string[]>([]);
  const [prevSelectedType, setPrevSelectedType] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedRow, setSelectedRow] = useState<number | undefined>(undefined);
  const [prevSearchTerm, setPrevSearchTerm] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const history = useHistory();
  // With only the right to read, no contacts are added
  const canWrite = useHasPermission(WRITE_CONTACTS);
  // The types of the filter as the admins have listed them; a chosen hidden type is kept
  const typeOptions = useContactTypeOptions(
    contactTypes,
    selectedType ? [selectedType] : []
  );

  const dispatch = useDispatch();
  const [exporting, setExporting] = useState(false);

  // All the contacts of the list with its search and type as an Excel file: the companies
  // and their persons on sheets of their own
  const exportExcel = async () => {
    setExporting(true);
    try {
      const response = await customerApi.export({
        search: searchTerm,
        texts: {
          companiesSheet: t('Contact Info'),
          companyColumns: [
            t('Company'),
            t('Business Id'),
            t('Street Address'),
            t('Postal Number'),
            t('Place'),
            t('Country'),
            t('Contact Types'),
            t('Person count'),
          ],
          fileName: t('export.File name'),
          personColumns: [
            t('Last Name'),
            t('First Name'),
            t('Title'),
            t('Job Description'),
            t(`${translations.common}:Email`),
            t('Phone'),
            t('Company'),
          ],
          personsSheet: t('Persons'),
          // The names of the types as the admins have named them
          types: Object.fromEntries(
            contactTypes.map((type) => [type, typeOptions.labelOf(type)])
          ),
        },
        type: selectedType,
      });

      // The name the server gave, e.g. Yhteystiedot_2026-10-15.xlsx
      const disposition = response.headers['content-disposition'] ?? '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = match ? decodeURIComponent(match[1]) : 'contacts.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(addMessage(t('export.Failed')));
    } finally {
      setExporting(false);
    }
  };

  const getTypes = () => {
    customerApi.getTypes().then((contactTypeData) => {
      setContactTypes(contactTypeData);
    });
  };

  // The company, its business id, the place of its address and how many persons it has
  const columns = [
    {
      dataField: 'companyName',
      text: t('Company'),
    },
    {
      dataField: 'businessId',
      text: t('Business Id'),
    },
    {
      dataField: 'city',
      text: t('Place'),
    },
    {
      dataField: 'contactPersonCount',
      text: t('Person count'),
    },
  ];

  const handleTableChange = (type: any, { page, sizePerPage }: any) => {
    setPage(page);
    setSizePerPage(sizePerPage);
    setOffset((page - 1) * sizePerPage);
  };

  const handleChange = (e: any) => {
    setSelectedType(e.target.value);
  };

  const createContact = () => {
    history.push('/contacts/create');
  };

  useEffect(() => getTypes(), []);

  useEffect(() => {
    const getContacts = async () => {
      try {
        if (
          (searchTerm !== '' && searchTerm !== prevSearchTerm) ||
          (selectedType !== '' && selectedType !== prevSelectedType)
        ) {
          setPage(1);
          setOffset(0);
        }
        setPrevSearchTerm(searchTerm);
        setPrevSelectedType(selectedType);
        const list = await customerApi.get({
          limit: sizePerPage,
          offset,
          type: selectedType,
          search: searchTerm,
        });
        const count = await customerApi.count(searchTerm, selectedType);
        setContactData(list);
        setTotalSize(count.count);
      } catch (error) {
        // define action
      }
    };
    getContacts();
  }, [offset, searchTerm, sizePerPage, selectedType]);

  useEffect(() => {
    if (selectedRow !== undefined && contactData.length > 0) {
      history.push(`/contacts/${contactData[selectedRow || 0].id}`);
    }
  }, [history, selectedRow, contactData]);

  return (
    <PageContainer>
      <Row>
        <Col>
          <p className="header-text">{t('common:Contacts')}</p>
        </Col>

        <Col className="header-add-btn button-margin-top">
          <PrimaryButton
            className="contacts-export-button"
            disabled={exporting}
            onClick={exportExcel}
          >
            {(exporting
              ? t('export.Exporting')
              : t('export.Button')
            ).toLocaleUpperCase()}
          </PrimaryButton>
          {canWrite ? (
            <PrimaryButton onClick={() => createContact()}>
              {t('common:Add').toLocaleUpperCase()}
            </PrimaryButton>
          ) : null}
        </Col>
      </Row>
      <div className="basic-table table-background-offers">
        <div className="search-type-row">
          <Form className="text-right-margin">
            <FormGroup>
              <Input
                className="text-field select-option"
                type="select"
                onChange={handleChange}
              >
                <option className="select-option" value="" key="all">
                  {t('common:All')}
                </option>
                {typeOptions.options.map((option) => (
                  <option
                    className="select-option"
                    value={option.value}
                    key={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Form>
          <SearchInput setSearchTerms={setSearchTerm} />
        </div>
        <BasicTable
          columns={columns}
          data={contactData || []}
          page={page}
          sizePerPage={sizePerPage}
          totalSize={totalSize}
          handleTableChange={handleTableChange}
          setSelectedRow={setSelectedRow}
          keyField="id"
        />
      </div>
    </PageContainer>
  );
};

export default Contacts;
